import { ImsPermissions } from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import { isWmsSotEnabledForAnyLocation } from "@redotech/redo-fulfillment-wms-common/wms-constants";
import { useTranslation } from "@redotech/redo-i18n";
import BarcodeLabelIcon from "@redotech/redo-icons/bar-chart-01.svg";
import BarcodeLabelIconFilled from "@redotech/redo-icons/bar-chart-01_filled.svg";
import ClipboardCheckIcon from "@redotech/redo-icons/clipboard-check.svg";
import ClipboardCheckIconFilled from "@redotech/redo-icons/clipboard-check_filled.svg";
import InventoryAdjustmentsIcon from "@redotech/redo-icons/inventory_adjustments.svg";
import LayersIcon from "@redotech/redo-icons/layers-two-01.svg";
import LayersIconFilled from "@redotech/redo-icons/layers-two-01_filled.svg";
import ForecastingIcon from "@redotech/redo-icons/line-chart-up-02.svg";
import ForecastingIconFilled from "@redotech/redo-icons/line-chart-up-02_filled.svg";
import PackageIcon from "@redotech/redo-icons/package.svg";
import PackageIconFilled from "@redotech/redo-icons/package_filled.svg";
import RefreshIcon from "@redotech/redo-icons/refresh-ccw-03_filled.svg";
import RefreshIconOutline from "@redotech/redo-icons/refresh-cw-02.svg";
import RepeatIcon from "@redotech/redo-icons/repeat-01.svg";
import RepeatIconFilled from "@redotech/redo-icons/repeat-01_filled.svg";
import ShoppingBagIcon from "@redotech/redo-icons/shopping-bag-03.svg";
import ShoppingBagIconFilled from "@redotech/redo-icons/shopping-bag-03_filled.svg";
import SuppliersIcon from "@redotech/redo-icons/suppliers.svg";
import {
  useHasPermission,
  useIsRedoAdmin,
} from "@redotech/redo-merchant-app-common/auth/permissions-context";
import {
  isCatalogEnabled,
  useCanAccessCatalogBillOfMaterials,
} from "@redotech/redo-merchant-app-common/catalog/catalog-access";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { useCapability } from "@redotech/redo-merchant-app-common/use-capability";
import { useTeamCanCycleCount } from "@redotech/redo-merchant-inventory-app/cycle-counts/cycle-counts-available";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CycleCountViews } from "./cycle-count-views";
import { IntelligentInventoryItem } from "./intelligent-inventory";
import { InventoryAnalyticsItem } from "./inventory-analytics";
import { InventoryLevelsItem } from "./inventory-levels";
import { PurchaseOrdersItem } from "./purchase-orders";
import { ShipmentsItem } from "./shipments";
import { TransfersItem } from "./transfers";

export const InventoryItem = memo(function InventoryItem() {
  const team = useRequiredContext(TeamContext);

  const imsEnabled = useCapability("ims");

  const isForecastingEnabled = team.settings.ims?.forecastingEnabled ?? false;
  const isForecastingBillingEnabled =
    team.settings.ims?.forecastingBilling?.enabled ?? false;

  const canAccessInventory = useHasPermission(ImsPermissions.ACCESS_INVENTORY);
  const canAccessTransfers = useHasPermission(ImsPermissions.ACCESS_TRANSFERS);

  const showForecastingOnlyPlans =
    !imsEnabled && (isForecastingEnabled || isForecastingBillingEnabled);
  const showInventoryUpsell =
    !imsEnabled && !isForecastingEnabled && !isForecastingBillingEnabled;

  return (
    <>
      <InventoryAnalyticsItem />
      <ForecastingItem />
      <IntelligentInventoryItem />
      <InventoryFolderItem
        canAccessInventory={canAccessInventory}
        hideTransfers={!imsEnabled || !canAccessTransfers}
      />
      {/* Unsubscribed merchants: "Inventory" is an upsell link to onboarding. */}
      {showInventoryUpsell && <InventoryUpsellItem />}
      {/* Forecasting-only merchants (no IMS): show onboarding Plans link. */}
      {showForecastingOnlyPlans && <InventoryPlansItem />}
      <PurchasingItem />
      <RecommerceItem />
    </>
  );
});

const ForecastingItem = memo(function ForecastingItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);
  const { pathname } = useLocation();

  const isForecastingEnabled = team.settings.ims?.forecastingEnabled ?? false;
  const isForecastingBillingEnabled =
    team.settings.ims?.forecastingBilling?.enabled ?? false;
  const canAccessForecasting = useHasPermission(
    ImsPermissions.ACCESS_FORECASTING,
  );
  const showForecastingFolder =
    (isForecastingEnabled || isForecastingBillingEnabled) &&
    canAccessForecasting;

  const isForecastingChildSelected = pathname.startsWith(
    `${routes.inventoryManagementBaseUrl}/forecasting`,
  );

  return (
    <RedoSecondaryNavItem
      action={{ type: "button" }}
      anyChildSelected={isForecastingChildSelected}
      FilledIcon={ForecastingIconFilled}
      friendlyName={t("Forecasting", {
        context: "inventory navigation label for stock demand forecasting",
      })}
      hidden={!showForecastingFolder}
      id="forecasting"
      isFolder
      UnfilledIcon={ForecastingIcon}
    >
      <ForecastingMonthlyItem />
      <ForecastingRecommendationsItem />
      <ForecastingRulesItem />
    </RedoSecondaryNavItem>
  );
});

const ForecastingMonthlyItem = memo(function ForecastingMonthlyItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/forecasting/monthly`,
      }}
      friendlyName={t("Monthly forecast", {
        context:
          "inventory navigation label for the monthly stock demand forecast",
      })}
      id="forecasting-monthly"
      isNew
    />
  );
});

const ForecastingRecommendationsItem = memo(
  function ForecastingRecommendationsItem() {
    const { t } = useTranslation();
    const routes = useRequiredContext(MerchantRoutesContext);

    return (
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: `${routes.inventoryManagementBaseUrl}/forecasting/recommendations`,
        }}
        beta
        friendlyName={t("Purchasing recommendations", {
          context: "inventory navigation label for recommended stock purchases",
        })}
        id="forecasting-recommendations"
      />
    );
  },
);

const ForecastingRulesItem = memo(function ForecastingRulesItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/forecasting/rules`,
      }}
      beta
      friendlyName={t("Rules", {
        context: "inventory forecasting navigation page for purchasing rules",
      })}
      id="forecasting-rules"
    />
  );
});

const InventoryFolderItem = memo(function InventoryFolderItem({
  canAccessInventory,
  hideTransfers,
}: {
  canAccessInventory: boolean;
  hideTransfers: boolean;
}) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);
  const { pathname } = useLocation();

  const imsEnabled = useCapability("ims");

  const isForecastingEnabled = team.settings.ims?.forecastingEnabled ?? false;
  const isForecastingBillingEnabled =
    team.settings.ims?.forecastingBilling?.enabled ?? false;

  const base = routes.inventoryManagementBaseUrl;
  const isInventoryChildSelected =
    pathname.startsWith(`${base}/intelligent-cycle-counts`) ||
    pathname.startsWith(`${base}/adjustments`) ||
    pathname.startsWith(`${base}/products`) ||
    pathname.startsWith(`${base}/barcode-label-templates`) ||
    pathname.startsWith(`${base}/transfers`);

  const showInventoryFolder =
    imsEnabled || isForecastingEnabled || isForecastingBillingEnabled;
  const showInventoryLevels =
    (imsEnabled || isForecastingEnabled || isForecastingBillingEnabled) &&
    canAccessInventory;

  return (
    <RedoSecondaryNavItem
      action={{ type: "button" }}
      anyChildSelected={isInventoryChildSelected}
      FilledIcon={PackageIconFilled}
      friendlyName={t("Inventory", {
        context: "inventory and purchasing navigation label",
      })}
      hidden={!showInventoryFolder}
      id="inventory"
      isFolder
      UnfilledIcon={PackageIcon}
    >
      <BarcodeLabelTemplatesItem />
      <BillOfMaterialsItem />
      <CycleCountsItem />
      <InventoryAdjustmentsItem />
      {showInventoryLevels && <InventoryLevelsItem />}
      <TransfersItem hidden={hideTransfers} />
    </RedoSecondaryNavItem>
  );
});

const BarcodeLabelTemplatesItem = memo(function BarcodeLabelTemplatesItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  const canAccessInventory = useHasPermission(ImsPermissions.ACCESS_INVENTORY);
  const isRedoEmployee = useIsRedoAdmin();

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/barcode-label-templates`,
      }}
      FilledIcon={BarcodeLabelIconFilled}
      friendlyName={t("Barcode label templates", {
        context: "inventory and purchasing navigation label",
      })}
      hidden={!canAccessInventory || !isRedoEmployee}
      id="barcode-label-templates"
      UnfilledIcon={BarcodeLabelIcon}
    />
  );
});

/** Bills of materials moved to Catalog; this item explains the move and links there. */
const BillOfMaterialsItem = memo(function BillOfMaterialsItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);
  const navigate = useNavigate();
  const [movedModalOpen, setMovedModalOpen] = useState(false);

  const imsEnabled = useCapability("ims");
  const canAccessBillOfMaterials = useHasPermission(
    ImsPermissions.ACCESS_BILL_OF_MATERIALS,
  );
  const canOpenInCatalog = useCanAccessCatalogBillOfMaterials();

  const unavailableMessage = isCatalogEnabled(team)
    ? t("You don't have access to Catalog. Ask a store admin for access.", {
        context:
          "shown when a user cannot open bills of materials in Catalog because they lack Catalog permissions",
      })
    : t("Catalog isn't enabled for your store. Contact support to enable it.", {
        context:
          "shown when a store cannot open bills of materials in Catalog because Catalog is turned off",
      });

  const closeModal = () => setMovedModalOpen(false);

  return (
    <>
      <RedoSecondaryNavItem
        action={{ type: "button", onClick: () => setMovedModalOpen(true) }}
        FilledIcon={LayersIconFilled}
        friendlyName={t("Bill of materials", {
          context: "inventory and purchasing navigation label",
        })}
        hidden={!imsEnabled || !canAccessBillOfMaterials}
        id="bill-of-materials"
        UnfilledIcon={LayersIcon}
      />
      {movedModalOpen && (
        <RedoModal
          isOpen
          onModalCloseRequested={closeModal}
          primaryButton={{
            text: t("Go to Catalog", {
              context: "button that opens bills of materials in Catalog",
            }),
            onClick: () => {
              closeModal();
              navigate(`${routes.catalogBaseUrl}/bill-of-materials`);
            },
            disabled: canOpenInCatalog ? false : unavailableMessage,
          }}
          secondaryButton={{
            text: t("Close", { context: "button that closes a dialog" }),
            onClick: closeModal,
          }}
          title={t("Bill of materials moved to Catalog", {
            context:
              "dialog title explaining that bills of materials moved from Inventory to Catalog",
          })}
        >
          <Flex dir="column" gap="sm">
            <Text fontSize="sm" fontWeight="regular" textColor="secondary">
              {t(
                "Bills of materials now live in Catalog, alongside your products.",
                {
                  context:
                    "dialog body explaining that bills of materials moved from Inventory to Catalog",
                },
              )}
            </Text>
            {!canOpenInCatalog && (
              <Text fontSize="sm" fontWeight="regular" textColor="secondary">
                {unavailableMessage}
              </Text>
            )}
          </Flex>
        </RedoModal>
      )}
    </>
  );
});

const CycleCountsItem = memo(function CycleCountsItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const { pathname } = useLocation();

  const imsEnabled = useCapability("ims");
  const canAccessCycleCounts = useHasPermission(
    ImsPermissions.ACCESS_CYCLE_COUNTS,
  );
  const teamCanCycleCount = useTeamCanCycleCount();

  return (
    <RedoSecondaryNavItem
      action={{ type: "button" }}
      anyChildSelected={pathname.startsWith(
        `${routes.inventoryManagementBaseUrl}/intelligent-cycle-counts`,
      )}
      FilledIcon={ClipboardCheckIconFilled}
      friendlyName={t("Cycle counts", {
        context: "inventory and purchasing navigation label",
      })}
      hidden={!imsEnabled || !canAccessCycleCounts || !teamCanCycleCount}
      id="intelligent-cycle-counts"
      isFolder
      isNew
      UnfilledIcon={ClipboardCheckIcon}
    >
      <CycleCountViews />
      <CycleCountRulesItem />
    </RedoSecondaryNavItem>
  );
});

const CycleCountRulesItem = memo(function CycleCountRulesItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  const canManageCycleCountRules = useHasPermission(
    ImsPermissions.MANAGE_CYCLE_COUNT_RULES,
  );

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/intelligent-cycle-counts/rules`,
      }}
      FilledIcon={RepeatIconFilled}
      friendlyName={t("Cycle count rules", {
        context: "inventory and purchasing navigation label",
      })}
      hidden={!canManageCycleCountRules}
      id="cycle-count-rules"
      UnfilledIcon={RepeatIcon}
    />
  );
});

const InventoryAdjustmentsItem = memo(function InventoryAdjustmentsItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);
  const wmsSourceOfTruth = isWmsSotEnabledForAnyLocation(
    team.settings.outboundLabels?.wms,
  );

  const imsEnabled = useCapability("ims");
  const canAccessInventory = useHasPermission(ImsPermissions.ACCESS_INVENTORY);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/adjustments`,
      }}
      FilledIcon={InventoryAdjustmentsIcon}
      friendlyName={t("Inventory adjustments", {
        context: "inventory and purchasing navigation label",
      })}
      hidden={!imsEnabled || !canAccessInventory || wmsSourceOfTruth}
      id="inventory-adjustments"
      UnfilledIcon={InventoryAdjustmentsIcon}
    />
  );
});

/** Upsell link shown to merchants without any inventory subscription. */
const InventoryUpsellItem = memo(function InventoryUpsellItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/onboarding`,
      }}
      FilledIcon={PackageIconFilled}
      friendlyName={t("Inventory", {
        context: "inventory and purchasing navigation label",
      })}
      id="inventory-upsell"
      UnfilledIcon={PackageIcon}
    />
  );
});

/** Onboarding link shown to forecasting-only merchants (no IMS). */
const InventoryPlansItem = memo(function InventoryPlansItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/onboarding`,
      }}
      FilledIcon={PackageIconFilled}
      friendlyName={t("Plans", {
        context: "inventory navigation page for inventory planning",
      })}
      id="inventory-plans"
      UnfilledIcon={PackageIcon}
    />
  );
});

const PurchasingItem = memo(function PurchasingItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const { pathname } = useLocation();

  const imsEnabled = useCapability("ims");
  const canAccessPurchaseOrders = useHasPermission(
    ImsPermissions.ACCESS_PURCHASE_ORDERS,
  );
  const canAccessShipments = useHasPermission(ImsPermissions.ACCESS_SHIPMENTS);

  const base = routes.inventoryManagementBaseUrl;
  const isPurchasingChildSelected =
    pathname.startsWith(`${base}/purchase-orders`) ||
    pathname.startsWith(`${base}/shipments`) ||
    pathname.startsWith(`${base}/suppliers`) ||
    pathname.startsWith(`${base}/document-templates`);

  return (
    <RedoSecondaryNavItem
      action={{ type: "button" }}
      anyChildSelected={isPurchasingChildSelected}
      FilledIcon={ShoppingBagIconFilled}
      friendlyName={t("Purchasing", {
        context: "inventory and purchasing navigation label",
      })}
      hidden={!imsEnabled}
      id="purchasing"
      isFolder
      UnfilledIcon={ShoppingBagIcon}
    >
      {canAccessPurchaseOrders && <PurchaseOrdersItem />}
      {canAccessShipments && <ShipmentsItem />}
      <SuppliersItem />
    </RedoSecondaryNavItem>
  );
});

const SuppliersItem = memo(function SuppliersItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const { pathname } = useLocation();

  const canAccessSuppliers = useHasPermission(ImsPermissions.ACCESS_SUPPLIERS);

  const base = routes.inventoryManagementBaseUrl;
  const isSuppliersChildSelected =
    pathname.startsWith(`${base}/suppliers`) ||
    pathname.startsWith(`${base}/document-templates`);

  return (
    <RedoSecondaryNavItem
      action={{ type: "button" }}
      anyChildSelected={isSuppliersChildSelected}
      FilledIcon={SuppliersIcon}
      friendlyName={t("Suppliers", {
        context: "inventory and purchasing navigation label",
      })}
      hidden={!canAccessSuppliers}
      id="suppliers"
      isFolder
      UnfilledIcon={SuppliersIcon}
    >
      <AllSuppliersItem />
      <PoTemplatesItem />
    </RedoSecondaryNavItem>
  );
});

const AllSuppliersItem = memo(function AllSuppliersItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/suppliers`,
      }}
      friendlyName={t("All suppliers", {
        context: "inventory and purchasing navigation label",
      })}
      id="suppliers-all"
    />
  );
});

const PoTemplatesItem = memo(function PoTemplatesItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  const canAccessPurchaseOrders = useHasPermission(
    ImsPermissions.ACCESS_PURCHASE_ORDERS,
  );

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/document-templates`,
      }}
      friendlyName={t("PO templates", {
        context: "inventory navigation label for purchase order templates",
      })}
      hidden={!canAccessPurchaseOrders}
      id="document-templates"
    />
  );
});

const RecommerceItem = memo(function RecommerceItem() {
  const { t } = useTranslation();
  const team = useRequiredContext(TeamContext);
  const { pathname } = useLocation();

  const isMarketplaceEnabled = team.settings.recommerce?.marketplace ?? false;
  const isRecommerceChildSelected =
    pathname.includes("/liquidation/") || pathname.endsWith("/liquidation");

  return (
    <RedoSecondaryNavItem
      action={{ type: "button" }}
      anyChildSelected={isRecommerceChildSelected}
      FilledIcon={RefreshIcon}
      friendlyName={t("Recommerce", {
        context:
          "inventory navigation label for resale and recommerce listings",
      })}
      hidden={!isMarketplaceEnabled}
      id="recommerce"
      isFolder
      UnfilledIcon={RefreshIconOutline}
    >
      <LiquidationDashboardItem />
      <LiquidationAllListingsItem />
    </RedoSecondaryNavItem>
  );
});

const LiquidationDashboardItem = memo(function LiquidationDashboardItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/liquidation/dashboard`,
      }}
      friendlyName={t("Dashboard", {
        context: "inventory and purchasing navigation label",
      })}
      id="liquidation-dashboard"
    />
  );
});

const LiquidationAllListingsItem = memo(function LiquidationAllListingsItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/liquidation/products`,
      }}
      friendlyName={t("All listings", {
        context: "inventory and purchasing navigation label",
      })}
      id="liquidation-all-products"
    />
  );
});
